import { getIn } from "formik";
import Select from "orion/lib/select";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Option } from "./async-select.types";

const orderOptions = (values: Option[] = []): Option[] => {
  const fixedOptions = (values || []).filter((option) => option.isFixed);
  const nonFixedOptions = (values || []).filter((option) => !option.isFixed);
  return [...fixedOptions, ...nonFixedOptions];
};

export const asyncSelectType = {
  storageRegions: "storageRegions",
};

const updateFixedOptions = (initialValues: Option[], options: Option[]) => {
  return options.map((option: Option) => {
    return {
      ...option,
      isFixed: !!initialValues?.find(
        (inVal: Option) => inVal.value === option.value
      )?.isFixed,
    };
  });
};

const AsyncSelect = ({
  name,
  formikProps,
  isMulti,
  type,
  onChange,
  disabled,
  isClearable,
  iterator,
  data,
  isLoading,
  ...rest
}: any) => {
  const initialValue = useMemo(() => {
    const rawInitialValue = formikProps
      ? getIn(formikProps.values, name)
      : rest.value;
    return isMulti ? orderOptions(rawInitialValue) : rawInitialValue;
  }, []);

  const selectRef = useRef<HTMLSelectElement>(null);

  const options = useMemo(() => {
    return updateFixedOptions(initialValue, iterator?.(data) || data);
  }, [data, type, initialValue, iterator]);

  const styles = useMemo(() => {
    return {
      multiValueRemove: (base: any) => {
        return base.data.isFixed ? { display: "none" } : {};
      },
      multiValueLabel: (base: any) => {
        return base.data.isFixed ? { paddingRight: "6px" } : {};
      },
    };
  }, [type]);

  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (isMulti && value.length === options.length && options.length > 0) {
      selectRef?.current?.blur();
    }
  }, [isMulti, options?.length, value?.length]);

  const onChangeHandler = useCallback(
    // here type should be ActionMeta<Option> but it is not defined in current @types/orion
    // TODO: Add type ActionMeta<Option> when upgrading orion to v8
    (value: Option | Option[], { action, removedValue }: any) => {
      let updatedValue = value;
      if (isMulti && Array.isArray(updatedValue)) {
        switch (action) {
          case "remove-value":
          case "pop-value":
            if (removedValue.isFixed) {
              updatedValue = [...updatedValue, removedValue];
            }
            break;
          case "clear":
            updatedValue = options.filter((v: Option) => v.isFixed);
            break;
        }
        setValue(orderOptions(updatedValue));
        formikProps?.setFieldValue(name, orderOptions(updatedValue));
      } else {
        setValue(updatedValue);
      }
      onChange?.(updatedValue);
    },
    [formikProps, isMulti, name, onChange, options]
  );

  useEffect(() => {
    setValue(getIn(formikProps.values, name));
  }, [formikProps.values, name]);

  return (
    <Select
      onChange={onChangeHandler}
      isMulti={isMulti}
      name={name}
      type="select"
      options={options}
      validationProps={formikProps}
      value={value}
      styles={styles}
      ref={selectRef}
      isClearable={
        isClearable || (isMulti && value.some((v: Option) => !v.isFixed))
      }
      isDisabled={disabled}
      isLoading={isLoading}
      {...rest}
    />
  );
};

export default AsyncSelect;
